import React, { useState, useEffect, useRef } from 'react';
import { Search, X, FileText, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'sonner';

function UniversalSearch() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const searchInputRef = useRef(null);

  // Handle keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus search input when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Search function with debounce
  useEffect(() => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    const searchTimeout = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await api.get('/cases', {
          params: {
            search: searchQuery,
            limit: 10
          }
        });
        
        const cases = response.data.cases || [];
        setResults(cases.map(c => ({
          type: 'case',
          id: c.case_id,
          title: `Case ${c.case_id.substring(5, 17)}`,
          subtitle: `${c.vehicle_make || ''} ${c.vehicle_model || ''} ${c.vehicle_year || ''}`.trim() || 'Vehicle info unavailable',
          meta: `Trip: ${c.trip_id || 'N/A'} • Member: ${c.member_id || 'N/A'}`,
          status: c.status
        })));
      } catch (error) {
        console.error('Search failed:', error);
        toast.error('Search failed');
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [searchQuery]);

  const handleResultClick = (result) => {
    if (result.type === 'case') {
      navigate(`/cases/${result.id}`);
      setIsOpen(false);
      setSearchQuery('');
      setResults([]);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'open': 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400',
      'pending_review': 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
      'closed': 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
      'archived': 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
      'rejected': 'bg-rose-100 text-rose-700 dark:bg-rose-900/20 dark:text-rose-400'
    };
    return colors[status] || colors['open'];
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 transition-colors"
        title="Search (Cmd/Ctrl + K)"
      >
        <Search className="w-4 h-4" />
        <span className="hidden sm:inline text-sm">Search</span>
        <span className="hidden md:inline text-xs text-slate-400 dark:text-slate-500 ml-1">
          ⌘K
        </span>
      </button>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 backdrop-blur-sm"
        onClick={() => setIsOpen(false)}
      />

      {/* Search Modal */}
      <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4">
        <div 
          className="w-full max-w-2xl bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Search Input */}
          <div className="flex items-center gap-3 p-4 border-b border-slate-200 dark:border-slate-700">
            <Search className="w-5 h-5 text-slate-400" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search cases by ID, vehicle, trip, or member..."
              className="flex-1 bg-transparent text-slate-900 dark:text-slate-100 placeholder-slate-400 outline-none text-lg"
            />
            {loading && (
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-slate-300 border-t-orange-600" />
            )}
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Results */}
          <div className="max-h-[60vh] overflow-y-auto">
            {searchQuery && results.length === 0 && !loading && (
              <div className="p-8 text-center text-slate-500 dark:text-slate-400">
                <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-lg font-medium">No results found</p>
                <p className="text-sm mt-1">Try searching by case ID, vehicle, trip ID, or member ID</p>
              </div>
            )}

            {!searchQuery && (
              <div className="p-8 text-center text-slate-500 dark:text-slate-400">
                <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-lg font-medium">Quick Navigation</p>
                <p className="text-sm mt-1">Search for cases by ID, vehicle info, trip, or member</p>
                <div className="mt-4 flex items-center justify-center gap-2 text-xs">
                  <kbd className="px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded border border-slate-300 dark:border-slate-600">⌘K</kbd>
                  <span>or</span>
                  <kbd className="px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded border border-slate-300 dark:border-slate-600">Ctrl+K</kbd>
                  <span>to open</span>
                </div>
              </div>
            )}

            {results.length > 0 && (
              <div className="py-2">
                {results.map((result, index) => (
                  <button
                    key={result.id}
                    onClick={() => handleResultClick(result)}
                    className="w-full px-4 py-3 flex items-start gap-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors text-left group"
                  >
                    <div className="mt-1 p-2 rounded-lg bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 group-hover:bg-violet-200 dark:group-hover:bg-violet-900/50 transition-colors">
                      <FileText className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-slate-900 dark:text-slate-100 truncate">
                          {result.title}
                        </p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(result.status)}`}>
                          {result.status.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-400 truncate">
                        {result.subtitle}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-500 mt-1 truncate">
                        {result.meta}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-800 rounded border border-slate-300 dark:border-slate-600">↑</kbd>
                <kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-800 rounded border border-slate-300 dark:border-slate-600">↓</kbd>
                <span>Navigate</span>
              </div>
              <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-800 rounded border border-slate-300 dark:border-slate-600">Enter</kbd>
                <span>Select</span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-800 rounded border border-slate-300 dark:border-slate-600">Esc</kbd>
              <span>Close</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default UniversalSearch;
